import { ComponentProps } from 'lib/component-props';
import { ComponentRendering, Text, Field } from '@sitecore-jss/sitecore-jss-nextjs';
import { useMemo } from 'react';
import Rating from 'components/includes/Rating';
import styles from './CustomerReviewTile.module.scss';
import useHelpers from 'hooks/helpers';
import useComponentIndex from 'hooks/useComponentIndex';

type CustomerReviewTileFields = {
  title: Field<string>;
  description: Field<string>;
  authorName: Field<string>;
  stars: Field<string>;
  starsDescription: Field<string>;
};

type GridData = {
  count: number;
  columns: number[];
};

type CustomerReviewTileProps = ComponentProps & {
  fields: CustomerReviewTileFields;
  rendering: ComponentRendering;
  gridData?: GridData;
  className?: string;
};

const CustomerReviewTile: React.FC<CustomerReviewTileProps> = ({ fields, rendering, gridData }) => {
  const { backgroundClass } = useHelpers(rendering);
  const rating = useMemo(() => {
    return fields?.stars?.value ? parseInt(fields.stars.value, 10) : 0;
  }, [fields?.stars?.value]);

  const { ref, componentIndex } = useComponentIndex<HTMLDivElement>();
  const ratingDescription = useMemo(() => {
    if (!fields?.starsDescription?.value) return '';
    return fields.starsDescription.value.replace('{token.stars}', rating.toString());
  }, [fields?.starsDescription?.value, rating]);

  const cols = useMemo(() => {
    const numOfCols = Number(rendering?.params?.size) ?? 12;
    let mdColumns = 12;

    if (gridData?.columns.includes(4) || gridData?.columns.includes(8)) {
      mdColumns = 6;
    }

    const isLast = componentIndex === gridData?.count;
    const isOdd = (gridData?.count || 0) % 2;

    if (isOdd && isLast) {
      mdColumns = 12;
    }

    return `col-12 col-md-${mdColumns} col-xl-${numOfCols}`;
  }, [rendering?.params?.size, gridData?.columns, gridData?.count, componentIndex]);

  return (
    <div className={`${cols}`} ref={ref}>
      <div className={`p-5 w-100 ${backgroundClass}`}>
        {rating > 0 && (
          <div className={`${styles.rating} background-to-change d-flex align-items-center mb-5`}>
            <Rating rating={rating} className="flex-shring-0" />
            {ratingDescription && <span className="ms-2">{ratingDescription}</span>}
          </div>
        )}

        <Text field={fields.title} tag="h4" className="background-to-change" />

        <Text
          field={fields.description}
          tag="blockquote"
          className={`${styles.blockquote} mt-5 text-grey background-to-change`}
        />

        <Text
          field={fields.authorName}
          tag="p"
          className="section-header-large background-to-change mt-3"
        />
      </div>
    </div>
  );
};

export default CustomerReviewTile;
